import {
    IWellnessExamSalesItem, SalesItemModel,
    SalesPackageModel, SalesPlanModel
} from "@common/models";
import { DiscountItemTypes, SalesItemType, Traits } from "@common/models/Enums";
import { SalesBuilderSearchStore } from "@hospital/stores/SalesBuilderSearchStore";
import { computed, makeObservable, observable } from "mobx";

export interface ISalesStore {
    salesItems: IWellnessExamSalesItem[];
    salesPackages: SalesPackageModel[];
    salesPlans: SalesPlanModel[];
    regularSalesItems: IWellnessExamSalesItem[];
    discountedSalesItems: IWellnessExamSalesItem[];
    prescribeOnlySalesItems: IWellnessExamSalesItem[];
    totalRegularSalesItems: number;
    totalDiscountSalesItems: number;
    totalSalesPackageCount: number;
    totalSalesPlanCount: number;
    totalPrescribeOnlySalesItems: number;
    filterToSalesItemsThatCanBeAddedByUsers: (salesItems: SalesItemModel[]) => SalesItemModel[];
    filterToSalesItemsThatIsAnesethieaMedication: (salesItems: SalesItemModel[]) => SalesItemModel[]
    filterToSalesItemsForOutpatientPrescriptions: (salesItems: SalesItemModel[]) => SalesItemModel[]
    mapSalesItemsForVisitBuilder: (salesItems: SalesItemModel[]) => IWellnessExamSalesItem[];
}

export abstract class SalesStore implements ISalesStore {
    @observable salesItems: IWellnessExamSalesItem[] = [];
    @observable salesPackages: SalesPackageModel[] = [];
    @observable salesPlans: SalesPlanModel[] = [];
    @observable visitBuilderSearch: SalesBuilderSearchStore = new SalesBuilderSearchStore(this);

    constructor() {
        makeObservable(this, undefined, { autoBind: true });
    }

    /**
     * This computed will update and return only regular (non-discount) sales items whenever salesItems updates
     * Note: This logic was previously deep down inside the ExamFormVisitBuilder, moved it up for ease of use.
     */
    @computed get regularSalesItems() {
        const items = (this.salesItems ?? []).filter(si => !DiscountItemTypes.includes(si.salesItemTypeId));
        return _.orderBy(items, ["name", "sortOrder"]);
    }

    /**
     * This computed will updated and return only discount sales items whenever salesItems updates
     */
    @computed get discountedSalesItems() {
        const items = (this.salesItems ?? []).filter(si => DiscountItemTypes.includes(si.salesItemTypeId))
        return _.orderBy(items, ["name", "sortOrder"])
    }

    /**
    * This computed will update and return sales items that have a trait of Prescribe Only whenever salesItems updates
    */
    @computed get prescribeOnlySalesItems() {
        const items = (this.salesItems ?? []).filter(si => si.traits?.some(t => t.traitId === Traits.AllowPrescribeOnly));
        return _.orderBy(items, ["name", "sortOrder"]);
    }

    @computed get totalRegularSalesItems() {
        return this.regularSalesItems.length;
    }

    @computed get totalDiscountSalesItems() {
        return this.discountedSalesItems.length;
    }

    @computed get totalSalesPackageCount() {
        return this.salesPackages.length;
    }

    @computed get totalSalesPlanCount() {
        return this.salesPlans.length;
    }
    
    @computed get totalPrescribeOnlySalesItems() {
        return this.prescribeOnlySalesItems.length;
    }

    filterToSalesItemsThatCanBeAddedByUsers = (salesItems: SalesItemModel[]) =>
        salesItems.filter(i => !i.traits.some(t => t.traitId == Traits.BlockUsersFromManuallyAddingToVisit)); // BlockUsersFromManuallyAddingToVisit = 2

    filterToSalesItemsThatIsAnesethieaMedication = (salesItems: SalesItemModel[]) =>
        salesItems.filter(i => i.traits.some(t => t.traitId == Traits.IsAnesthesiaMedication)); //  Is Anesethiea Medication

    filterToSalesItemsForOutpatientPrescriptions = (salesItems: SalesItemModel[]) =>
        salesItems.filter(i => [SalesItemType.PrescriptionProduct, SalesItemType.VetcoPrescription].some(t => t === i.salesItemTypeId)
            && !i.traits.some(t => t.traitId == Traits.AllowedInPnpNewOrder));

    filterToSalesItemsForPetNotPresent = (salesItems: SalesItemModel[]) =>
        salesItems.filter(i => i.traits.some(t => t.traitId == Traits.AllowedInPnpNewOrder));

    filterToDiscountSalesItems = (salesItems: SalesItemModel[]) =>
        salesItems.filter(si => DiscountItemTypes.includes(si.salesItemTypeId))

    mapSalesItemsForVisitBuilder = (salesItems: SalesItemModel[]) =>
        salesItems.flatMap(i => i.options.map(o => ({
            salesItemId: o.salesItemId,
            salesItemOptionId: o.salesItemOptionId,
            salesItemTypeId: i.salesItemTypeId,
            salesSubCategoryId: i.salesSubCategoryId,
            name: i.name,
            option: o.displayName,
            price: i.prices.find(p => p.salesItemOptionId == null || p.salesItemOptionId == o.salesItemOptionId)!.price,
            dispensingFee: i.priceRules.find(pr => pr.salesItemOptionId == null || pr.salesItemOptionId == o.salesItemOptionId)!.dispensingFee,
            sortOrder: o.sortOrder,
            searchPattern: `${i.name} ${o.displayName}`.toLowerCase(),
            priceRules: i.priceRules,
            hospitalSKU: o.hospitalSKU,
            petcoSKU: o.petcoSKU,
            traits: i.traits,
            salesItemUpgradeId: i.salesItemUpgradeId,
            reminderDays: o.reminderDays,
            helpPhoto: i.helpPhoto,
            isPricedByOption: i.isPricedByOption,
            salesItemAttributes: i.salesItemAttributes,
            optionModel: o,
            salesItemRequirements : i.requirements

        })))
}
